import { Logger } from '../../../shared/utils/logger.js'
import { HERO_OUTLINE } from '../../../shared/constants/heroOutline.js'

/**
 * Collectively classifies all path IDs from a visualization SVG (main.svg)
 * into the outline type each represents.
 *
 * Pure-numeric IDs (e.g. `"12"`) are ambiguous in isolation — they could be a
 * building id_cad (`"10"`, `"11"`, `"12"` in a 12-building project) or a
 * tier ref_tier (`"0"`, `"9"`, `"12"` in a single-building project).
 * This function resolves the ambiguity by checking against actual data:
 *   1. If a numeric ID matches a known building id_cad → building
 *   2. Otherwise, if `parseInt(id)` matches a known ref_tier → tier
 *
 * Non-ambiguous formats are classified by format alone:
 * - `{2chars}-{digits}` → tier (canonical)
 * - UUID (5+ hyphen parts) → apartment
 * - 1-2 char alphanumeric matching a building id_cad → building
 *
 * TODO: Discuss with team — main.svg should adopt grouped structure
 *       (like outlines_*.svg) to eliminate ID-based guessing.
 *
 * @param {string[]}  ids        — raw `id` attributes from all SVG paths
 * @param {object}    data
 * @param {object[]}  data.buildings — from CatalogContext
 * @param {object[]}  data.tiers     — from CatalogContext
 * @returns {Map<string, string>}  Map<pathId, HERO_OUTLINE.*>
 */
export function classifyVisPathIds(ids, { buildings = [], tiers = [] }) {
  const buildingIdCadSet = new Set(buildings.map(b => b.id_cad))
  const tierRefTierSet   = new Set(tiers.map(t => String(t.ref_tier)))
  const result = new Map()

  for (const id of ids) {
    if (!id || typeof id !== 'string') continue

    const parts = id.split('-')

    // UUID-like: 5+ hyphen-separated segments → apartment
    if (parts.length >= 5) { result.set(id, HERO_OUTLINE.APARTMENTS); continue }

    // Canonical tier: "{building_id_cad}-{zfill_ref_tier}"
    if (/^[0-9A-Za-z]{2}-\d{2,}$/.test(id)) { result.set(id, HERO_OUTLINE.TIERS); continue }

    // Pure numeric → ambiguous, resolve with data
    if (/^\d+$/.test(id)) {
      // Zero-padded multi-char numeric (e.g. "00", "08") → building id_cad by convention
      if (id.length > 1 && id[0] === '0') { result.set(id, HERO_OUTLINE.BUILDINGS); continue }
      if (buildingIdCadSet.has(id)) { result.set(id, HERO_OUTLINE.BUILDINGS); continue }
      if (tierRefTierSet.has(String(parseInt(id, 10)))) { result.set(id, HERO_OUTLINE.TIERS); continue }
      continue
    }

    // 1-2 char alphanumeric → building
    if (/^[0-9A-Za-z]{1,2}$/.test(id)) { result.set(id, HERO_OUTLINE.BUILDINGS); continue }
  }

  return result
}

/**
 * Rewrites integer tier path IDs in a visualization SVG container to the
 * canonical `"{building_id_cad}-{zfill_ref_tier}"` format expected by
 * `useSvgTierInteraction`.
 *
 * Only pure-integer IDs are rewritten. Already-canonical IDs (`"0C-12"`),
 * building IDs (`"0A"`) and UUIDs are left untouched.
 *
 * Paths that cannot be resolved to a tier in `tiers` are left as-is — the
 * interaction hook will simply skip them (transparent, non-interactive).
 *
 * Must run after SVG injection (`dangerouslySetInnerHTML`) and before wiring
 * interaction listeners.
 *
 * @param {Element}  containerEl — DOM element holding the injected SVG
 * @param {object}   data
 * @param {object[]} data.buildings — from CatalogContext
 * @param {object[]} data.tiers     — from CatalogContext
 */
export function normalizeVisPathIds(containerEl, { buildings, tiers }) {
  if (!containerEl) return

  const paths = containerEl.querySelectorAll('path[id]')
  if (!paths.length) return

  // Build ref_tier → tier[] lookup
  const tiersByRefTier = new Map()
  for (const tier of tiers ?? []) {
    if (tier.ref_tier == null) continue
    if (!tiersByRefTier.has(tier.ref_tier)) tiersByRefTier.set(tier.ref_tier, [])
    tiersByRefTier.get(tier.ref_tier).push(tier)
  }

  const buildingIdCadSet = new Set((buildings ?? []).map(b => b.id_cad))
  const isSingleBuilding = (buildings?.length ?? 0) === 1
  const defaultBuildingId = isSingleBuilding ? buildings[0].id_cad : null

  for (const path of paths) {
    const id = path.id
    if (!id || !/^\d+$/.test(id)) continue

    // Zero-padded numeric IDs (e.g. "00", "08") are building id_cad values —
    // plain integers never use leading zeros. Skip without touching them.
    if (id.length > 1 && id[0] === '0') continue

    // Data-driven disambiguation: if this numeric ID is a known building
    // id_cad (e.g. "10", "11", "12" in a multi-building project), skip it.
    if (buildingIdCadSet.has(id)) continue

    const refTier   = parseInt(id, 10)
    const candidates = tiersByRefTier.get(refTier)
    if (!candidates?.length) continue

    let match = null

    if (candidates.length === 1) {
      match = candidates[0]
    } else if (defaultBuildingId) {
      match = candidates.find(t => t.building_id === defaultBuildingId) ?? null
    }

    if (match) {
      path.setAttribute('id', match.id_cad)
    } else {
      Logger.warning({
        message: 'Ambiguous vis tier path — multiple buildings share ref_tier',
        content: `ref_tier=${refTier}, candidates=${candidates.length}`,
      })
    }
  }
}
